import React, {useState, useEffect} from 'react'
import {Box, Typography} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import Button from 'src/components-new/primitives/Button'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import DataTable from 'src/components-new/data/DataTable'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'
import {CalendarEvent, mergeDateAndTime} from './agendaUtils'
import {buildParticipantColumns, ParticipantRow} from './virtualMeetingsColumns'

interface VirtualMeetingDetailNewProps {
  event: CalendarEvent | undefined
  onClose: () => void
}

/**
 * VirtualMeetingDetailNew is reused for both the virtual-meetings list (whose
 * participants nest user data under `userProperties`) and the calendar/agenda
 * events (whose participants carry the same fields flat). Read either shape.
 */
function toParticipantRows(participants: Record<string, any>[] | undefined): ParticipantRow[] {
  return (participants ?? []).map((participant, index) => ({
    key: participant.userProperties?._id ?? participant._id ?? participant.id ?? String(index),
    firstName: participant.userProperties?.firstName ?? participant.firstName,
    lastName: participant.userProperties?.lastName ?? participant.lastName,
    email: participant.userProperties?.email ?? participant.email,
    qualification: participant.userProperties?.qualification ?? participant.qualification,
  }))
}

export default function VirtualMeetingDetailNew({
  event,
  onClose,
}: VirtualMeetingDetailNewProps): JSX.Element {
  const toast = useToast()
  const {detailColumnOptions} = useAppSelector(
    (state) => state.pspConfigReducer.data.virtualMeetingsConfigurations
  )
  const participantColumns = buildParticipantColumns(detailColumnOptions)

  const [title, setTitle] = useState(event?.title ?? '')
  const [eventDate, setEventDate] = useState<string | null>(event?.startDate ?? null)
  const [startTime, setStartTime] = useState<string | null>(event?.startDate ?? null)
  const [endTime, setEndTime] = useState<string | null>(event?.endDate ?? null)
  const [status, setStatus] = useState<string | undefined>()
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    setTitle(event?.title ?? '')
    setEventDate(event?.startDate ?? null)
    setStartTime(event?.startDate ?? null)
    setEndTime(event?.endDate ?? null)
    setSubmitted(false)
    setStatus(undefined)
  }, [event])

  const handleSave = async () => {
    setSubmitted(true)
    if (!event?._id || !title.trim() || !eventDate || !startTime || !endTime) return

    setStatus(STATUS.PENDING)
    const body = {
      title,
      startDate: mergeDateAndTime(eventDate, startTime),
      endDate: mergeDateAndTime(eventDate, endTime),
    }
    let finalStatus: string | undefined
    let errorStatusCode: number | undefined
    const captureStatus = (s: string | undefined) => {
      finalStatus = s
      setStatus(s)
    }
    await apiClient.patchVirtualMeetings(event._id, body, captureStatus, (_message, statusCode) => {
      errorStatusCode = statusCode
    })
    if (finalStatus === STATUS.FULFILLED) {
      toast.success('Videoconsulto aggiornato.')
      onClose()
    } else if (errorStatusCode === 403) {
      toast.error(
        'Per modificare questa attività contatta la Centrale Operativa di Italiassistenza',
        {duration: 8000}
      )
    } else {
      toast.error('Errore durante il salvataggio del videoconsulto.')
    }
  }

  const handleOpenLink = () => {
    if (!event?.linkTeleconsultation) return
    window.open(event.linkTeleconsultation, '_blank', 'noopener,noreferrer')
  }

  const handleCopyLink = async () => {
    if (!event?.linkTeleconsultation) return
    await navigator.clipboard.writeText(event.linkTeleconsultation)
    toast.success('Link copiato negli appunti.')
  }

  return (
    <AppDialog
      actions={
        <>
          <Button onClick={onClose} variant="secondary">Annulla</Button>
          <Button loading={status === STATUS.PENDING} onClick={handleSave} variant="primary">Salva</Button>
        </>
      }
      maxWidth="md"
      onClose={onClose}
      open={true}
      title={title || "Incontro virtuale"}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
          gap: 2,
          pt: 1,
        }}
      >
        <TextFieldNew
          error={submitted && !title.trim()}
          fullWidth
          helperText={submitted && !title.trim() ? 'Campo obbligatorio' : undefined}
          label="Titolo"
          onChange={setTitle}
          required
          sx={{gridColumn: '1 / -1'}}
          value={title}
        />
        <TimePickerNew
          error={submitted && !startTime}
          fullWidth
          helperText={submitted && !startTime ? 'Campo obbligatorio' : undefined}
          label="Dalle"
          onChange={setStartTime}
          required
          value={startTime}
        />
        <TimePickerNew
          error={submitted && !endTime}
          fullWidth
          helperText={submitted && !endTime ? 'Campo obbligatorio' : undefined}
          label="Alle"
          onChange={setEndTime}
          required
          value={endTime}
        />
        <DatePickerNew
          error={submitted && !eventDate}
          fullWidth
          helperText={submitted && !eventDate ? 'Campo obbligatorio' : undefined}
          label="Data"
          onChange={setEventDate}
          required
          value={eventDate}
        />
        <Box sx={{gridColumn: '1 / -1', display: 'flex', alignItems: 'flex-end', gap: 1}}>
          <Box sx={{flexGrow: 1, minWidth: 0}}>
            <TextFieldNew
              disabled
              fullWidth
              label="Link per il collegamento"
              onChange={() => undefined}
              value={event?.linkTeleconsultation ?? ''}
            />
          </Box>
          <ActionIconButton
            disabled={!event?.linkTeleconsultation}
            onClick={handleOpenLink}
            tooltip="Apri il link del videoconsulto"
            variant="videocam"
          />
          <ActionIconButton
            disabled={!event?.linkTeleconsultation}
            onClick={handleCopyLink}
            tooltip="Copia il link"
            variant="copy"
          />
        </Box>
      </Box>

      <Box sx={{mt: 3}}>
        <Typography
          sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, display: 'block'}}
          variant="caption"
        >
          Partecipanti
        </Typography>
        <DataTable<ParticipantRow>
          columns={participantColumns}
          emptyMessage="Nessun partecipante"
          keyField="key"
          rows={toParticipantRows(event?.participants)}
        />
      </Box>
    </AppDialog>
  )
}
